import clsx from "clsx"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { StarIcon, Ton3dIcon } from "@/assets"
import {
  useClaimReferralRewardsMutation,
  useGetMyInfluencerQuery,
  useGetMyReferralsQuery
} from "@/store/apis/referralAPI"
import { Button } from "@/ui/Button"
import { Pagination } from "@/ui/Pagination"
import { FRIENDS_PAGE_LIMIT } from "../../values"
import { FriendCard } from "../FriendCard"
import styles from "./FriendsGrid.module.scss"

const SKELETON_KEYS = Array.from({ length: 8 }, (_, i) => i)

export const FriendsGrid: FC = () => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  const { data, isFetching, isError } = useGetMyReferralsQuery({
    page,
    limit: FRIENDS_PAGE_LIMIT
  })
  const { data: influencer } = useGetMyInfluencerQuery()
  const [claim, { isLoading: isCollecting }] = useClaimReferralRewardsMutation()

  const items = data?.items
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / FRIENDS_PAGE_LIMIT))
  const starsAvailable = influencer?.starsAvailableToClaim ?? 0
  const tonAvailable = influencer?.tonAvailableToClaim ?? 0

  useEffect(() => {
    if (!data) return
    if (page > totalPages) setPage(totalPages)
  }, [data, page, totalPages])

  if (isFetching && !items) {
    return (
      <section className={styles.FriendsGrid}>
        <ul className={styles.FriendsGrid_list}>
          {SKELETON_KEYS.map((key) => (
            <li key={key} className={styles.FriendsGrid_skeleton} />
          ))}
        </ul>
      </section>
    )
  }

  if (isError) {
    return (
      <section className={styles.FriendsGrid}>
        <p className={styles.FriendsGrid_message}>
          {t("referrals.friends.error")}
        </p>
      </section>
    )
  }

  if (total === 0) {
    return (
      <section className={styles.FriendsGrid}>
        <p className={styles.FriendsGrid_message}>
          {t("referrals.friends.empty")}
        </p>
      </section>
    )
  }

  const canCollect = !isCollecting && (starsAvailable > 0 || tonAvailable > 0)

  return (
    <section className={styles.FriendsGrid}>
      <div className={styles.FriendsGrid_listWrapper}>
        <ul
          className={clsx(styles.FriendsGrid_list, {
            [styles.isFetching]: isFetching
          })}
        >
          {items?.map((item) => (
            <FriendCard key={item._id} item={item} />
          ))}
        </ul>
      </div>
      <footer className={styles.FriendsGrid_footer}>
        <div className={styles.FriendsGrid_footer_left}>
          <div className={styles.FriendsGrid_footerTotals}>
            <span className={styles.FriendsGrid_footerTotal}>
              <StarIcon className={styles.FriendsGrid_footerTotalIcon} />
              {starsAvailable}
            </span>
            <span className={styles.FriendsGrid_footerTotal}>
              <Ton3dIcon className={styles.FriendsGrid_footerTotalIcon} />
              {tonAvailable}
            </span>
          </div>
          <Button
            styles="green"
            disabled={!canCollect}
            isLoading={isCollecting}
            onClick={() => claim()}
          >
            {t("referrals.friends.footer.collect")}
          </Button>
        </div>
        <Pagination
          page={page}
          total={total}
          perPage={FRIENDS_PAGE_LIMIT}
          onPageChange={setPage}
        />
      </footer>
    </section>
  )
}
